import {useState} from 'react'
import {Check, Edit, Settings, X} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Switch} from '@/components/ui/switch'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {FormGrid} from '@/components/shared/FormGrid'
import {Stack} from '@/components/shared/Stack'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Icon} from '@/components/shared/Icon'
import {CodeText} from '@/components/shared/CodeText'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import type {OutboundConnector, EmailOutboundConfigCreateRequest, EmailOutboundConfigUpdateRequest} from '@/types/outbound-connector'

 
const BODY_CONTENT_TYPES = [
    {value: 'text/html', label: 'text/html'},
    {value: 'text/plain', label: 'text/plain'},
] as const
const DEFAULT_BODY_CONTENT_TYPE = 'text/html'
 

interface EmailFormState {
    smtpHost: string
    smtpPort: number
    smtpUsername: string
    smtpPasswordRef: string
    useTls: boolean
    fromAddress: string
    toAddresses: string
    ccAddresses: string
    grantSubjectTemplate: string
    grantBodyTemplate: string
    revokeSubjectTemplate: string
    revokeBodyTemplate: string
    bodyContentType: string
    connectTimeoutMs: number
    version: number
}

const DEFAULT_EMAIL_FORM: EmailFormState = {
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPasswordRef: '',
    useTls: true,
    fromAddress: '',
    toAddresses: '',
    ccAddresses: '',
    grantSubjectTemplate: '',
    grantBodyTemplate: '',
    revokeSubjectTemplate: '',
    revokeBodyTemplate: '',
    bodyContentType: DEFAULT_BODY_CONTENT_TYPE,
    connectTimeoutMs: 5000,
    version: 0,
}

interface EmailConfigCardProps {
    connector: OutboundConnector
    canUpdate: boolean
}

export function EmailConfigCard({connector, canUpdate}: EmailConfigCardProps) {
    const services = useServices()
    const i18n = useI18n()
    const createDialog = useDialog()
    const editDialog = useDialog()
    const [form, setForm] = useState<EmailFormState>(DEFAULT_EMAIL_FORM)

    const updateForm = (updates: Partial<EmailFormState>) => setForm((prev) => ({...prev, ...updates}))

    const createMutation = useEntityMutation({
        mutationFn: (data: EmailOutboundConfigCreateRequest) =>
            services.outboundConnectors.createEmailConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: createDialog.close,
    })

    const updateMutation = useEntityMutation({
        mutationFn: (data: EmailOutboundConfigUpdateRequest) =>
            services.outboundConnectors.updateEmailConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: editDialog.close,
    })

    const openCreate = () => {
        setForm(DEFAULT_EMAIL_FORM)
        createDialog.open()
    }

    const openEdit = () => {
        const config = connector.emailConfig
        if (!config) return
        setForm({
            smtpHost: config.smtpHost,
            smtpPort: config.smtpPort,
            smtpUsername: config.smtpUsername ?? '',
            smtpPasswordRef: config.smtpPasswordRef ?? '',
            useTls: config.useTls,
            fromAddress: config.fromAddress,
            toAddresses: config.toAddresses,
            ccAddresses: config.ccAddresses ?? '',
            grantSubjectTemplate: config.grantSubjectTemplate,
            grantBodyTemplate: config.grantBodyTemplate,
            revokeSubjectTemplate: config.revokeSubjectTemplate,
            revokeBodyTemplate: config.revokeBodyTemplate,
            bodyContentType: config.bodyContentType,
            connectTimeoutMs: config.connectTimeoutMs,
            version: config.version,
        })
        editDialog.open()
    }

    const handleCreate = () => {
        const request: EmailOutboundConfigCreateRequest = {
            smtpHost: form.smtpHost,
            smtpPort: form.smtpPort || undefined,
            smtpUsername: form.smtpUsername || undefined,
            smtpPasswordRef: form.smtpPasswordRef || undefined,
            useTls: form.useTls,
            fromAddress: form.fromAddress,
            toAddresses: form.toAddresses,
            ccAddresses: form.ccAddresses || undefined,
            grantSubjectTemplate: form.grantSubjectTemplate,
            grantBodyTemplate: form.grantBodyTemplate,
            revokeSubjectTemplate: form.revokeSubjectTemplate,
            revokeBodyTemplate: form.revokeBodyTemplate,
            bodyContentType: form.bodyContentType || undefined,
            connectTimeoutMs: form.connectTimeoutMs || undefined,
        }
        createMutation.mutate(request)
    }

    const handleUpdate = () => {
        const request: EmailOutboundConfigUpdateRequest = {
            smtpHost: form.smtpHost,
            smtpPort: form.smtpPort,
            smtpUsername: form.smtpUsername || null,
            smtpPasswordRef: form.smtpPasswordRef || null,
            useTls: form.useTls,
            fromAddress: form.fromAddress,
            toAddresses: form.toAddresses,
            ccAddresses: form.ccAddresses || null,
            grantSubjectTemplate: form.grantSubjectTemplate,
            grantBodyTemplate: form.grantBodyTemplate,
            revokeSubjectTemplate: form.revokeSubjectTemplate,
            revokeBodyTemplate: form.revokeBodyTemplate,
            bodyContentType: form.bodyContentType || null,
            connectTimeoutMs: form.connectTimeoutMs,
            version: form.version,
        }
        updateMutation.mutate(request)
    }

    const isFormValid = () =>
        !!form.smtpHost && !!form.fromAddress && !!form.toAddresses
        && !!form.grantSubjectTemplate && !!form.grantBodyTemplate
        && !!form.revokeSubjectTemplate && !!form.revokeBodyTemplate

    const emailConfig = connector.emailConfig

    const formFields = (mode: 'create' | 'edit') => {
        const handleSubmit = () => {
            if (mode === 'create') {
                handleCreate()
            } else {
                handleUpdate()
            }
        }
        return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            <Stack gap="lg">
                {/* SMTP Settings */}
                <Stack>
                    <Strong>{i18n.connectors.smtpSettings}</Strong>
                    <DetailGrid columns={2} responsive={false}>
                        <FormGrid>
                            <Label htmlFor="email-smtpHost">{i18n.connectors.smtpHost}</Label>
                            <Input id="email-smtpHost" value={form.smtpHost}
                                onChange={(e) => updateForm({smtpHost: e.target.value})}
                                placeholder={i18n.connectors.smtpHostPlaceholder}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="email-smtpPort">{i18n.connectors.smtpPort}</Label>
                            <Input id="email-smtpPort" type="number" min={1} max={65535} value={form.smtpPort}
                                onChange={(e) => updateForm({smtpPort: parseInt(e.target.value) || 587})}/>
                        </FormGrid>
                    </DetailGrid>
                    <DetailGrid columns={2} responsive={false}>
                        <FormGrid>
                            <Label htmlFor="email-smtpUsername">{i18n.connectors.smtpUsername}</Label>
                            <Input id="email-smtpUsername" value={form.smtpUsername}
                                onChange={(e) => updateForm({smtpUsername: e.target.value})}
                                placeholder={i18n.connectors.optionalPlaceholder}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="email-smtpPasswordRef">{i18n.connectors.smtpPasswordRef}</Label>
                            <Input id="email-smtpPasswordRef" value={form.smtpPasswordRef}
                                onChange={(e) => updateForm({smtpPasswordRef: e.target.value})}
                                placeholder={i18n.connectors.smtpPasswordRefPlaceholder}/>
                        </FormGrid>
                    </DetailGrid>
                    <div className="items-center space-x-2">
                        <Switch id="email-useTls" checked={form.useTls}
                            onCheckedChange={(v) => updateForm({useTls: v})}/>
                        <Label htmlFor="email-useTls">{i18n.connectors.useTls}</Label>
                    </div>
                </Stack>

                {/* Recipients */}
                <Stack>
                    <Strong>{i18n.connectors.recipients}</Strong>
                    <FormGrid>
                        <Label htmlFor="email-fromAddress">{i18n.connectors.fromAddress}</Label>
                        <Input id="email-fromAddress" type="email" value={form.fromAddress}
                            onChange={(e) => updateForm({fromAddress: e.target.value})}
                            placeholder={i18n.connectors.fromAddressPlaceholder}/>
                    </FormGrid>
                    <FormGrid>
                        <Label htmlFor="email-toAddresses">{i18n.connectors.toAddresses}</Label>
                        <Input id="email-toAddresses" value={form.toAddresses}
                            onChange={(e) => updateForm({toAddresses: e.target.value})}
                            placeholder={i18n.connectors.toAddressesPlaceholder}/>
                    </FormGrid>
                    <FormGrid>
                        <Label htmlFor="email-ccAddresses">{i18n.connectors.ccAddresses}</Label>
                        <Input id="email-ccAddresses" value={form.ccAddresses}
                            onChange={(e) => updateForm({ccAddresses: e.target.value})}
                            placeholder={i18n.connectors.optionalPlaceholder}/>
                    </FormGrid>
                </Stack>

                {/* Grant Template */}
                <Stack>
                    <Strong>{i18n.connectors.grantTemplate}</Strong>
                    <FormGrid>
                        <Label htmlFor="email-grantSubject">{i18n.connectors.subjectTemplate}</Label>
                        <Input id="email-grantSubject" value={form.grantSubjectTemplate}
                            onChange={(e) => updateForm({grantSubjectTemplate: e.target.value})}
                            placeholder={i18n.connectors.grantSubjectTemplatePlaceholder}/>
                    </FormGrid>
                    <FormGrid>
                        <Label htmlFor="email-grantBody">{i18n.connectors.bodyTemplate}</Label>
                        <textarea id="email-grantBody" rows={4}
                            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                            value={form.grantBodyTemplate}
                            onChange={(e) => updateForm({grantBodyTemplate: e.target.value})}
                            placeholder={i18n.connectors.grantBodyTemplatePlaceholder}/>
                    </FormGrid>
                </Stack>

                {/* Revoke Template */}
                <Stack>
                    <Strong>{i18n.connectors.revokeTemplate}</Strong>
                    <FormGrid>
                        <Label htmlFor="email-revokeSubject">{i18n.connectors.subjectTemplate}</Label>
                        <Input id="email-revokeSubject" value={form.revokeSubjectTemplate}
                            onChange={(e) => updateForm({revokeSubjectTemplate: e.target.value})}
                            placeholder={i18n.connectors.revokeSubjectTemplatePlaceholder}/>
                    </FormGrid>
                    <FormGrid>
                        <Label htmlFor="email-revokeBody">{i18n.connectors.bodyTemplate}</Label>
                        <textarea id="email-revokeBody" rows={4}
                            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                            value={form.revokeBodyTemplate}
                            onChange={(e) => updateForm({revokeBodyTemplate: e.target.value})}
                            placeholder={i18n.connectors.revokeBodyTemplatePlaceholder}/>
                    </FormGrid>
                </Stack>

                {/* Advanced Settings */}
                <Stack>
                    <Strong>{i18n.connectors.advancedSettings}</Strong>
                    <DetailGrid columns={2} responsive={false}>
                        <FormGrid>
                            <Label htmlFor="email-bodyContentType">{i18n.connectors.bodyContentType}</Label>
                            <Select value={form.bodyContentType}
                                onValueChange={(v) => updateForm({bodyContentType: v})}>
                                <SelectTrigger data-testid="select-body-content-type"><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    {BODY_CONTENT_TYPES.map(({value, label}) => (
                                        <SelectItem key={value} value={value}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="email-connectTimeout">{i18n.connectors.connectTimeout}</Label>
                            <Input id="email-connectTimeout" type="number" min={1000} max={60000}
                                value={form.connectTimeoutMs}
                                onChange={(e) => updateForm({connectTimeoutMs: parseInt(e.target.value) || 5000})}/>
                        </FormGrid>
                    </DetailGrid>
                </Stack>
            </Stack>
            <SaveCancelFooter
                testIdPrefix={mode === 'create' ? 'create-email-config' : 'edit-email-config'}
                onCancel={() => mode === 'create' ? createDialog.close() : editDialog.close()}
                saving={mode === 'create' ? createMutation.isPending : updateMutation.isPending}
                disabled={!isFormValid()}
                saveLabel={mode === 'create' ? i18n.connectors.saveConfiguration : i18n.common.saveChanges}
            />
        </form>
        )
    }

    return (
        <>
            <Card data-testid="email-config-card">
                <RowCardHeader>
                    <div>
                        <CardTitle className="items-center gap-2" data-testid="card-title-email-config">
                            <Settings className="h-5 w-5"/>
                            {i18n.connectors.emailConfiguration}
                        </CardTitle>
                        <CardDescription>{i18n.connectors.emailDescription}</CardDescription>
                    </div>
                    {canUpdate && (
                        emailConfig ? (
                            <Button data-testid="btn-edit-email-config" size="sm" onClick={openEdit}>
                                <Icon icon={Edit} className="mr-2"/>
                                {i18n.common.edit}
                            </Button>
                        ) : (
                            <Button data-testid="btn-create-email-config" size="sm" onClick={openCreate}>
                                <Icon icon={Settings} className="mr-2"/>
                                {i18n.connectors.configureEmailConnection}
                            </Button>
                        )
                    )}
                </RowCardHeader>
                <CardContent>
                    {emailConfig ? (
                        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.smtpHost}</dt>
                                <dd><CodeText>{emailConfig.smtpHost}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.smtpPort}</dt>
                                <dd><CodeText>{emailConfig.smtpPort}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.useTls}</dt>
                                <dd className="mt-1">
                                    {emailConfig.useTls
                                        ? <Check className="h-4 w-4 text-status-success"/>
                                        : <Icon icon={X} muted/>}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.fromAddress}</dt>
                                <dd><CodeText>{emailConfig.fromAddress}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.toAddresses}</dt>
                                <dd><CodeText>{emailConfig.toAddresses}</CodeText></dd>
                            </div>
                            {emailConfig.ccAddresses && (
                                <div>
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.ccAddresses}</dt>
                                    <dd><CodeText>{emailConfig.ccAddresses}</CodeText></dd>
                                </div>
                            )}
                            {emailConfig.smtpUsername && (
                                <div>
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.smtpUsername}</dt>
                                    <dd><CodeText>{emailConfig.smtpUsername}</CodeText></dd>
                                </div>
                            )}
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.bodyContentType}</dt>
                                <dd><CodeText>{emailConfig.bodyContentType}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.connectTimeout}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: emailConfig.connectTimeoutMs})}</dd>
                            </div>
                        </dl>
                    ) : (
                        <p className="text-muted-foreground text-center py-4" data-testid="email-not-configured">
                            {i18n.connectors.emailNotConfigured}
                        </p>
                    )}
                </CardContent>
            </Card>

            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.configureEmailConnection}</DialogTitle>
                        <DialogDescription>{i18n.connectors.configureEmailDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('create')}
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.editEmailConfiguration}</DialogTitle>
                        <DialogDescription>{i18n.connectors.configureEmailDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('edit')}
                </DialogContent>
            </Dialog>
        </>
    )
}
